// src/contexts/NotificationContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  toast,
} from "sonner";

import {
  NOTIFICATION_SETTINGS_STORAGE_KEY,
  NOTIFICATIONS_STORAGE_KEY,
} from "../constants/Settings.constants";

import {
  NOTIFICATION_TEMPLATES,
  type NewNotification,
  type Notification,
  type NotificationContextType,
  type NotificationSettings,
  type NotificationSource,
  type NotificationType,
} from "./NotificationContext.types";

import {
  isNotificationPreferenceEnabled,
  loadNotificationPreferences,
  mergeNotificationPreferences,
  playNotificationSound as playSound,
  saveNotificationPreferences,
  type NotificationPreferencePath,
} from "../utils/notificationSound";

import type {
  NotificationPreferences,
} from "../types/Settings.types";

export type {
  Notification,
  NotificationContextType,
  NotificationPreferences,
  NotificationSettings,
  NotificationSource,
  NotificationType,
};

export {
  NOTIFICATION_TEMPLATES,
};

interface NotificationProviderProps {
  children: ReactNode;
}

const DEFAULT_NOTIFICATIONS:
  Notification[] = [];

const DEFAULT_SETTINGS:
  NotificationSettings = {
    soundEnabled:
      true,

    desktopEnabled:
      false,

    emailEnabled:
      false,

    smsEnabled:
      false,

    mutedSources:
      [],

    mutedTypes:
      [],
  };

const NOTIFICATION_TYPE_MAPPING:
  Partial<
    Record<
      NotificationType,
      NotificationPreferencePath
    >
  > = {
    test_request: {
      portal:
        "laboratory",

      key:
        "newTestRequests",
    },

    sample_collected: {
      portal:
        "laboratory",

      key:
        "sampleCollected",
    },

    result_ready: {
      portal:
        "laboratory",

      key:
        "completedTests",
    },

    completed: {
      portal:
        "laboratory",

      key:
        "completedTests",
    },

    quality_alert: {
      portal:
        "laboratory",

      key:
        "qualityAlerts",
    },

    equipment_alert: {
      portal:
        "laboratory",

      key:
        "equipmentAlerts",
    },

    low_inventory: {
      portal:
        "laboratory",

      key:
        "lowInventory",
    },

    claim: {
      portal:
        "insurance",

      key:
        "newClaims",
    },

    insurance_claim: {
      portal:
        "insurance",

      key:
        "newClaims",
    },

    claim_update: {
      portal:
        "insurance",

      key:
        "claimUpdates",
    },

    pre_auth: {
      portal:
        "insurance",

      key:
        "urgentPreAuths",
    },

    verification: {
      portal:
        "insurance",

      key:
        "completedVerifications",
    },

    enrollment_update: {
      portal:
        "insurance",

      key:
        "enrollmentUpdates",
    },

    appointment: {
      portal:
        "hospital",

      key:
        "newAppointments",
    },

    appointment_created: {
      portal:
        "hospital",

      key:
        "newAppointments",
    },

    appointment_updated: {
      portal:
        "hospital",

      key:
        "appointmentChanges",
    },

    appointment_cancelled: {
      portal:
        "hospital",

      key:
        "appointmentChanges",
    },

    consultation_reminder: {
      portal:
        "hospital",

      key:
        "consultationReminders",
    },

    reminder: {
      portal:
        "hospital",

      key:
        "consultationReminders",
    },

    patient_assigned: {
      portal:
        "hospital",

      key:
        "newPatientAssignments",
    },

    urgent_patient_alert: {
      portal:
        "hospital",

      key:
        "urgentPatientAlerts",
    },

    urgent: {
      portal:
        "hospital",

      key:
        "urgentPatientAlerts",
    },

    emergency_alert: {
      portal:
        "hospital",

      key:
        "urgentPatientAlerts",
    },

    test_result: {
      portal:
        "hospital",

      key:
        "testResultUpdates",
    },

    staff_update: {
      portal:
        "hospital",

      key:
        "staffAccountUpdates",
    },

    facility_update: {
      portal:
        "hospital",

      key:
        "facilityProfileUpdates",
    },

    update: {
      portal:
        "global",

      key:
        "systemUpdates",
    },

    system_update: {
      portal:
        "global",

      key:
        "systemUpdates",
    },
  };

const NotificationContext =
  createContext<
    NotificationContextType |
      undefined
  >(undefined);

const formatRelativeTime = (
  timestamp: number
): string => {
  const difference = Math.max(
    0,
    Date.now() - timestamp
  );

  const minutes = Math.floor(
    difference / 60_000
  );

  const hours = Math.floor(
    difference / 3_600_000
  );

  const days = Math.floor(
    difference / 86_400_000
  );

  if (minutes < 1) {
    return "Just now";
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  if (hours < 24) {
    return `${hours}h ago`;
  }

  if (days === 1) {
    return "1d ago";
  }

  return `${days}d ago`;
};

const mergePreferenceUpdates = (
  previous:
    NotificationPreferences,

  updates:
    Partial<NotificationPreferences>
): NotificationPreferences => {
  return {
    global: {
      ...previous.global,
      ...(updates.global ?? {}),
    },

    hospital: {
      ...previous.hospital,
      ...(updates.hospital ?? {}),
    },

    laboratory: {
      ...previous.laboratory,
      ...(updates.laboratory ?? {}),
    },

    insurance: {
      ...previous.insurance,
      ...(updates.insurance ?? {}),
    },
  };
};

const loadStoredNotifications =
  (): Notification[] => {
    try {
      const stored =
        window.localStorage.getItem(
          NOTIFICATIONS_STORAGE_KEY
        );

      if (!stored) {
        return DEFAULT_NOTIFICATIONS;
      }

      const parsed: unknown =
        JSON.parse(stored);

      return Array.isArray(parsed)
        ? parsed as Notification[]
        : DEFAULT_NOTIFICATIONS;
    } catch (error) {
      console.error(
        "Failed to load notifications:",
        error
      );

      return DEFAULT_NOTIFICATIONS;
    }
  };

const loadStoredSettings =
  (): NotificationSettings => {
    try {
      const stored =
        window.localStorage.getItem(
          NOTIFICATION_SETTINGS_STORAGE_KEY
        );

      if (!stored) {
        return DEFAULT_SETTINGS;
      }

      const parsed =
        JSON.parse(
          stored
        ) as Partial<NotificationSettings>;

      return {
        ...DEFAULT_SETTINGS,
        ...parsed,

        mutedSources:
          parsed.mutedSources ??
          DEFAULT_SETTINGS.mutedSources,

        mutedTypes:
          parsed.mutedTypes ??
          DEFAULT_SETTINGS.mutedTypes,
      };
    } catch (error) {
      console.error(
        "Failed to load notification settings:",
        error
      );

      return DEFAULT_SETTINGS;
    }
  };

export function NotificationProvider({
  children,
}: NotificationProviderProps) {
  const [
    notifications,
    setNotifications,
  ] = useState<Notification[]>(
    loadStoredNotifications
  );

  const [
    settings,
    setSettings,
  ] = useState<NotificationSettings>(
    loadStoredSettings
  );

  const [
    notificationPreferences,
    setNotificationPreferences,
  ] = useState<NotificationPreferences>(
    () =>
      mergeNotificationPreferences(
        loadNotificationPreferences()
      )
  );

  const [
    isNotificationPanelOpen,
    setIsNotificationPanelOpen,
  ] = useState(
    false
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        NOTIFICATIONS_STORAGE_KEY,
        JSON.stringify(
          notifications
        )
      );
    } catch (error) {
      console.error(
        "Failed to save notifications:",
        error
      );
    }
  }, [
    notifications,
  ]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        NOTIFICATION_SETTINGS_STORAGE_KEY,
        JSON.stringify(
          settings
        )
      );
    } catch (error) {
      console.error(
        "Failed to save notification settings:",
        error
      );
    }
  }, [
    settings,
  ]);

  useEffect(() => {
    saveNotificationPreferences(
      notificationPreferences
    );
  }, [
    notificationPreferences,
  ]);

  useEffect(() => {
    const interval =
      window.setInterval(
        () => {
          const now =
            Date.now();

          setNotifications(
            (previous) =>
              previous
                .filter(
                  (
                    notification
                  ) =>
                    !notification.expiresAt ||
                    notification.expiresAt >
                      now
                )
                .map(
                  (
                    notification
                  ) => ({
                    ...notification,

                    time:
                      formatRelativeTime(
                        notification.timestamp
                      ),
                  })
                )
          );
        },
        60_000
      );

    return () => {
      window.clearInterval(
        interval
      );
    };
  }, []);

  const playNotificationSound =
    useCallback(
      (
        notificationType?:
          NotificationType
      ): void => {
        if (
          !settings.soundEnabled
        ) {
          return;
        }

        if (
          notificationType
        ) {
          const preferencePath =
            NOTIFICATION_TYPE_MAPPING[
              notificationType
            ];

          if (
            preferencePath &&
            !isNotificationPreferenceEnabled(
              preferencePath,
              notificationPreferences
            )
          ) {
            return;
          }
        }

        playSound(
          0.1
        );
      },
      [
        notificationPreferences,
        settings.soundEnabled,
      ]
    );

  const showDesktopNotification =
    useCallback(
      (
        notification:
          Notification
      ): void => {
        if (
          !settings.desktopEnabled ||
          !(
            "Notification" in
            window
          ) ||
          window.Notification
            .permission !==
            "granted"
        ) {
          return;
        }

        try {
          new window.Notification(
            notification.title,
            {
              body:
                notification.message,

              icon:
                "/IMG/GluviaApp.PNG",

              badge:
                "/IMG/GluviaApp.PNG",

              tag:
                notification.id,
            }
          );
        } catch (error) {
          console.error(
            "Failed to show desktop notification:",
            error
          );
        }
      },
      [
        settings.desktopEnabled,
      ]
    );

  const isNotificationMuted =
    useCallback(
      (
        notification:
          NewNotification
      ): boolean => {
        if (
          settings
            .doNotDisturbStart &&
          settings
            .doNotDisturbEnd
        ) {
          const now =
            new Date();

          const currentTime =
            `${String(
              now.getHours()
            ).padStart(
              2,
              "0"
            )}:${String(
              now.getMinutes()
            ).padStart(
              2,
              "0"
            )}`;

          const isWithinDoNotDisturb =
            currentTime >=
              settings
                .doNotDisturbStart &&
            currentTime <=
              settings
                .doNotDisturbEnd;

          if (
            isWithinDoNotDisturb
          ) {
            return true;
          }
        }

        return (
          settings
            .mutedSources
            .includes(
              notification.source
            ) ||
          settings
            .mutedTypes
            .includes(
              notification.type
            )
        );
      },
      [
        settings,
      ]
    );

  const pushNotification =
    useCallback(
      (
        notification:
          NewNotification
      ): void => {
        if (
          isNotificationMuted(
            notification
          )
        ) {
          return;
        }

        const template =
          NOTIFICATION_TEMPLATES[
            notification.type
          ] ??
          NOTIFICATION_TEMPLATES
            .default;

        const timestamp =
          Date.now();

        const newNotification:
          Notification = {
            ...notification,

            id:
              `notification-${timestamp}-${Math.random()
                .toString(36)
                .slice(2, 10)}`,

            timestamp,

            time:
              formatRelativeTime(
                timestamp
              ),

            read:
              false,

            priority:
              notification.priority ??
              template.priority,

            color:
              notification.color ??
              template.color,

            bg:
              notification.bg ??
              template.bg,
          };

        setNotifications(
          (previous) => [
            newNotification,
            ...previous,
          ]
        );

        playNotificationSound(
          newNotification.type
        );

        showDesktopNotification(
          newNotification
        );

        const toastOptions = {
          description:
            newNotification.message,
        };

        if (
          newNotification.priority ===
          "critical"
        ) {
          toast.error(
            newNotification.title,
            toastOptions
          );

          return;
        }

        toast.success(
          newNotification.title,
          toastOptions
        );
      },
      [
        isNotificationMuted,
        playNotificationSound,
        showDesktopNotification,
      ]
    );

  const markAsRead =
    useCallback(
      (
        id: string
      ): void => {
        setNotifications(
          (previous) =>
            previous.map(
              (
                notification
              ) =>
                notification.id ===
                id
                  ? {
                      ...notification,

                      read:
                        true,
                    }
                  : notification
            )
        );
      },
      []
    );

  const markAllAsRead =
    useCallback(
      (): void => {
        setNotifications(
          (previous) =>
            previous.map(
              (
                notification
              ) => ({
                ...notification,

                read:
                  true,
              })
            )
        );

        toast.success(
          "All notifications marked as read"
        );
      },
      []
    );

  const deleteNotification =
    useCallback(
      (
        id: string
      ): void => {
        setNotifications(
          (previous) =>
            previous.filter(
              (
                notification
              ) =>
                notification.id !==
                id
            )
        );

        toast.success(
          "Notification deleted"
        );
      },
      []
    );

  const clearAllNotifications =
    useCallback(
      (): void => {
        setNotifications(
          []
        );

        window.localStorage
          .removeItem(
            NOTIFICATIONS_STORAGE_KEY
          );

        toast.success(
          "All notifications cleared"
        );
      },
      []
    );

  const updateSettings =
    useCallback(
      (
        updates:
          Partial<NotificationSettings>
      ): void => {
        setSettings(
          (previous) => ({
            ...previous,
            ...updates,
          })
        );
      },
      []
    );

  const toggleSound =
    useCallback(
      (): void => {
        setSettings(
          (previous) => {
            const soundEnabled =
              !previous
                .soundEnabled;

            if (
              soundEnabled
            ) {
              playSound(
                0.3
              );
            }

            toast.success(
              soundEnabled
                ? "Notification sound enabled"
                : "Notification sound disabled"
            );

            return {
              ...previous,
              soundEnabled,
            };
          }
        );
      },
      []
    );

  const toggleDesktop =
    useCallback(
      (): void => {
        if (
          !(
            "Notification" in
            window
          )
        ) {
          toast.error(
            "Desktop notifications are not supported by this browser."
          );

          return;
        }

        if (
          settings.desktopEnabled
        ) {
          setSettings(
            (previous) => ({
              ...previous,

              desktopEnabled:
                false,
            })
          );

          toast.success(
            "Desktop notifications disabled"
          );

          return;
        }

        void window.Notification
          .requestPermission()
          .then(
            (
              permission
            ) => {
              if (
                permission !==
                "granted"
              ) {
                toast.error(
                  "Desktop notification permission was denied."
                );

                return;
              }

              setSettings(
                (previous) => ({
                  ...previous,

                  desktopEnabled:
                    true,
                })
              );

              toast.success(
                "Desktop notifications enabled"
              );
            }
          );
      },
      [
        settings.desktopEnabled,
      ]
    );

  const updateNotificationPreferences =
    useCallback(
      (
        updates:
          Partial<NotificationPreferences>
      ): void => {
        setNotificationPreferences(
          (previous) =>
            mergePreferenceUpdates(
              previous,
              updates
            )
        );
      },
      []
    );

  const openNotificationPanel =
    useCallback(
      (): void => {
        setIsNotificationPanelOpen(
          true
        );
      },
      []
    );

  const closeNotificationPanel =
    useCallback(
      (): void => {
        setIsNotificationPanelOpen(
          false
        );
      },
      []
    );

  const toggleNotificationPanel =
    useCallback(
      (): void => {
        setIsNotificationPanelOpen(
          (previous) =>
            !previous
        );
      },
      []
    );

  const getNotificationsBySource =
    useCallback(
      (
        source:
          NotificationSource
      ): Notification[] => {
        return notifications
          .filter(
            (
              notification
            ) =>
              notification.source ===
              source
          );
      },
      [
        notifications,
      ]
    );

  const getNotificationsByType =
    useCallback(
      (
        type:
          NotificationType
      ): Notification[] => {
        return notifications
          .filter(
            (
              notification
            ) =>
              notification.type ===
              type
          );
      },
      [
        notifications,
      ]
    );

  const getUnreadNotifications =
    useCallback(
      (): Notification[] => {
        return notifications
          .filter(
            (
              notification
            ) =>
              !notification.read
          );
      },
      [
        notifications,
      ]
    );

  const unreadCount =
    useMemo(
      () =>
        notifications
          .filter(
            (
              notification
            ) =>
              !notification.read
          )
          .length,
      [
        notifications,
      ]
    );

  const value =
    useMemo<
      NotificationContextType
    >(
      () => ({
        notifications,
        unreadCount,

        pushNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,

        settings,

        soundEnabled:
          settings.soundEnabled,

        desktopEnabled:
          settings.desktopEnabled,

        updateSettings,
        toggleSound,
        toggleDesktop,

        notificationPreferences,
        updateNotificationPreferences,

        isNotificationPanelOpen,
        openNotificationPanel,
        closeNotificationPanel,
        toggleNotificationPanel,

        getNotificationsBySource,
        getNotificationsByType,
        getUnreadNotifications,
      }),
      [
        notifications,
        unreadCount,
        pushNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        settings,
        updateSettings,
        toggleSound,
        toggleDesktop,
        notificationPreferences,
        updateNotificationPreferences,
        isNotificationPanelOpen,
        openNotificationPanel,
        closeNotificationPanel,
        toggleNotificationPanel,
        getNotificationsBySource,
        getNotificationsByType,
        getUnreadNotifications,
      ]
    );

  return (
    <NotificationContext.Provider
      value={value}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications =
  (): NotificationContextType => {
    const context =
      useContext(
        NotificationContext
      );

    if (!context) {
      throw new Error(
        "useNotifications must be used within a NotificationProvider"
      );
    }

    return context;
  };